import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';
import { THEME_NAMES, PRESETS, openPdf } from '../utils/certificates';

const SLOT_LABEL = { 0: 'Background image (A4 landscape)', 1: 'Signature 1', 2: 'Signature 2' };

// One stored image (signature or background) with replace / remove.
const ImageSlot = ({ templateId, slot, present, onChanged }) => {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let revoke;
    if (present) {
      api.get(`/certificates/templates/${templateId}/images/${slot}`, { responseType: 'blob' })
        .then(r => { revoke = URL.createObjectURL(r.data); setUrl(revoke); })
        .catch(() => setUrl(null));
    } else {
      setUrl(null);
    }
    return () => revoke && URL.revokeObjectURL(revoke);
  }, [templateId, slot, present]);

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('image', file);
    setBusy(true);
    try {
      await api.put(`/certificates/templates/${templateId}/images/${slot}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    await api.delete(`/certificates/templates/${templateId}/images/${slot}`);
    onChanged();
  };

  return (
    <div className="flex flex-center gap-2" style={{ minHeight: 44 }}>
      {url
        ? <img src={url} alt={SLOT_LABEL[slot]} style={{ height: 40, maxWidth: 140, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff' }} />
        : <span className="text-sm text-muted">None</span>}
      <label className="btn btn-secondary btn-sm" style={{ margin: 0 }}>
        {busy ? 'Uploading...' : url ? 'Replace' : 'Upload'}
        <input type="file" accept="image/png,image/jpeg" hidden onChange={e => upload(e.target.files[0])} />
      </label>
      {url && <button type="button" className="btn btn-secondary btn-sm" onClick={remove}>Remove</button>}
    </div>
  );
};

const CertificateTemplateModal = ({ template, onClose, onSaved, readOnly }) => {
  const [form, setForm] = useState(() => {
    const base = template || PRESETS.achievement;
    const sigs = [...(base.signatories || [])];
    while (sigs.length < 2) sigs.push({ name: '', title: '' });
    return { ...base, signatories: sigs };
  });
  const [id, setId] = useState(template?.id || null);
  const [slots, setSlots] = useState(template?.image_slots || []);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setSig = (i, k, v) => setForm(f => ({
    ...f, signatories: f.signatories.map((s, j) => (j === i ? { ...s, [k]: v } : s))
  }));

  const payload = () => ({ ...form, signatories: form.signatories.filter(s => s.name || s.title) });

  const refreshSlots = async () => {
    const { data } = await api.get('/certificates/templates');
    setSlots(data.find(t => t.id === id)?.image_slots || []);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = id
        ? await api.put(`/certificates/templates/${id}`, payload())
        : await api.post('/certificates/templates', payload());
      const wasNew = !id;
      setId(data.id);
      onSaved(data);
      if (!wasNew) onClose();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const preview = () => openPdf(
    (opts) => api.post('/certificates/templates/preview', { ...payload(), id, sample_company: form.sample_company ?? 'Customer Company' }, opts),
    'Could not create the preview'
  );

  const applyPreset = (key) => {
    const p = PRESETS[key];
    const presetNames = Object.values(PRESETS).map(x => x.name);
    setForm(f => ({ ...p, name: !f.name || presetNames.includes(f.name) ? p.name : f.name, signatories: f.signatories }));
  };

  return (
    <div className="modal-overlay" {...overlayDismiss(onClose)}>
      <div className="modal" style={{ maxWidth: 760, maxHeight: '92vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h3>{readOnly ? 'Certificate template' : id ? 'Edit certificate template' : 'New certificate template'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={save}>
          <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
            <div className="modal-body">
              {!id && !readOnly && (
                <div className="flex flex-center gap-2 mb-3">
                  <span className="text-sm text-muted">Start from:</span>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => applyPreset('achievement')}>Completion wording</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => applyPreset('attendance')}>Attendance wording</button>
                </div>
              )}
              <div className="grid grid-2">
                <div className="form-group">
                  <label>Template name</label>
                  <input type="text" required maxLength={255} value={form.name} onChange={e => set('name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Number prefix</label>
                  <input type="text" maxLength={20} value={form.number_prefix} onChange={e => set('number_prefix', e.target.value.toUpperCase())} />
                  <small className="text-muted">e.g. LP-CPI gives LP-CPI-2610-K7M2QX</small>
                </div>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label>Design</label>
                  <select value={form.theme} onChange={e => set('theme', e.target.value)}>
                    {Object.entries(THEME_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Accent colour</label>
                  <input type="color" value={form.accent_color} onChange={e => set('accent_color', e.target.value)} style={{ height: 40, padding: 2 }} />
                </div>
              </div>

              <h4 className="mt-2 mb-2">Wording</h4>
              <p className="text-sm text-muted mb-3">
                You can use {'{course}'}, {'{company}'}, {'{dates}'}, {'{issued}'}, {'{score}'} and {'{employee_id}'};
                they are filled in for each person.
              </p>
              <div className="form-group">
                <label>Small heading</label>
                <input type="text" maxLength={100} value={form.kicker} onChange={e => set('kicker', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Title</label>
                <input type="text" maxLength={255} value={form.heading} onChange={e => set('heading', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Line before the name</label>
                <input type="text" maxLength={255} value={form.lead_text} onChange={e => set('lead_text', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Statement after the name</label>
                <textarea rows={3} maxLength={2000} style={{ fontFamily: 'inherit' }} value={form.statement} onChange={e => set('statement', e.target.value)} />
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label>Company line</label>
                  <input type="text" maxLength={255} value={form.company_text} onChange={e => set('company_text', e.target.value)} />
                  <small className="text-muted">Printed only when the exam or training names a company</small>
                </div>
                <div className="form-group">
                  <label>Details line</label>
                  <input type="text" maxLength={255} value={form.details} onChange={e => set('details', e.target.value)} />
                  <small className="text-muted">e.g. {'{dates}'} · 5 days · 40 hours</small>
                </div>
              </div>
              <div className="flex gap-4 mb-3" style={{ flexWrap: 'wrap' }}>
                <label className="flex flex-center gap-2 text-sm">
                  <input type="checkbox" checked={form.show_number} onChange={e => set('show_number', e.target.checked)} />
                  Certificate number and verification QR code
                </label>
                <label className="flex flex-center gap-2 text-sm">
                  <input type="checkbox" checked={form.show_employee_id} onChange={e => set('show_employee_id', e.target.checked)} />
                  Employee ID
                </label>
                <label className="flex flex-center gap-2 text-sm">
                  <input type="checkbox" checked={form.show_score} onChange={e => set('show_score', e.target.checked)} />
                  Score
                </label>
              </div>

              <h4 className="mt-2 mb-2">Signatories</h4>
              {form.signatories.map((s, i) => (
                <div key={i} className="mb-3" style={{ borderTop: '1px solid #f3f4f6', paddingTop: 10 }}>
                  <div className="grid grid-2">
                    <div className="form-group" style={{ marginBottom: 6 }}>
                      <label>Name {i + 1}</label>
                      <input type="text" maxLength={100} value={s.name} onChange={e => setSig(i, 'name', e.target.value)} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 6 }}>
                      <label>Title</label>
                      <input type="text" maxLength={150} value={s.title} onChange={e => setSig(i, 'title', e.target.value)} placeholder="e.g. Trainer, Light Pharma" />
                    </div>
                  </div>
                  {id && !readOnly && <ImageSlot templateId={id} slot={i + 1} present={slots.includes(i + 1)} onChanged={refreshSlots} />}
                </div>
              ))}
              {form.theme === 'custom' && id && !readOnly && (
                <div className="form-group">
                  <label>{SLOT_LABEL[0]}</label>
                  <ImageSlot templateId={id} slot={0} present={slots.includes(0)} onChanged={refreshSlots} />
                  <small className="text-muted">PNG or JPEG, up to 5 MB, 297 × 210 mm proportions. The text is printed on top.</small>
                </div>
              )}
              {!id && !readOnly && (
                <p className="text-sm text-muted">Save the template first, then you can add signature images{form.theme === 'custom' ? ' and the background' : ''}.</p>
              )}
            </div>
          </fieldset>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={preview}>Preview PDF</button>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn btn-secondary" onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</button>
            {!readOnly && <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CertificateTemplateModal;
